import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { PrismBanner } from "@prism-ui/react";
import { toast } from "../../../Toaster/toast";
import { apiError } from "./RatingWindowTab";

const FIELDS: [string, string][] = [
  ["mask_name", "Name"],
  ["mask_email", "Email"],
  ["mask_title", "Title"],
  ["mask_department", "Department"],
  ["mask_manager", "Manager"],
  ["mask_hire_date", "Hire date / tenure"],
  ["mask_location", "Location"],
  ["mask_photo", "Photo"],
];

type Config = Record<string, boolean | number>;

const VisibilityTab = ({ chapterId }: { chapterId: number }) => {
  const [config, setConfig] = useState<Config | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/blind_review_config", { params: { chapter_id: chapterId } });
      setConfig(data);
    } catch (err) {
      toast.error(apiError(err, "Unable to load visibility settings"));
    }
  }, [chapterId]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (field: string, value: boolean) => {
    const previous = config;
    setConfig({ ...(config as Config), [field]: value });
    try {
      const { data } = await axios.put(`/api/blind_review_config/${chapterId}`, { [field]: value });
      setConfig(data);
      if (field === "enabled") {
        toast.success(value ? "Blind review turned on for this chapter." : "Blind review turned off for this chapter.");
      } else {
        const label = FIELDS.find(([f]) => f === field)?.[1] ?? field;
        toast.success(`${label} is now ${value ? "hidden from" : "shown to"} raters.`);
      }
    } catch (err) {
      setConfig(previous);
      toast.error(apiError(err, "Unable to save visibility settings"));
    }
  };

  if (!config) return <p>Loading…</p>;

  return (
    <div>
      <PrismBanner tone="info">
        Blind review hides the selected applicant details from raters while they rate. It hides them in the
        Application Rater screen only; it is a display setting, not a data restriction.
      </PrismBanner>
      <p className="application-rater-autosave">Changes save automatically.</p>
      <label className="application-rater-toggle">
        <input
          type="checkbox"
          checked={!!config.enabled}
          onChange={(e) => update("enabled", e.target.checked)}
        />
        <strong>Enable blind review for this chapter</strong>
      </label>
      <fieldset disabled={!config.enabled} className="application-rater-fieldset">
        <legend>Hide from raters</legend>
        {FIELDS.map(([field, label]) => (
          <label key={field} className="application-rater-toggle">
            <input
              type="checkbox"
              checked={!!config[field]}
              onChange={(e) => update(field, e.target.checked)}
            />
            {label}
          </label>
        ))}
      </fieldset>
    </div>
  );
};

export default VisibilityTab;
